/**
 * CCHModulePage — Community Care Hub module page
 *
 * All 13 CCH modules with real API data fetching, detail drill-down,
 * status transition actions, and back navigation.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  ClipboardList, Shield, Target, DollarSign, ShieldCheck,
  ClipboardCheck, AlertTriangle, TrendingUp, Users, MapPin,
  Home, Award, BarChart3, Building2, ArrowLeft,
  ChevronRight, Eye, Check, X, Clock, Calendar, FileText, RefreshCw,
  Link2, ExternalLink,
} from 'lucide-react';
import { apiClient } from '@/services/apiClient';
import { useTabbedChrome } from '@/components/weavein';

interface CCHModulePageProps {
  module: string;
  onNavigate?: (tab: string) => void;
}

const MODULE_META: Record<string, { title: string; description: string; icon: React.ElementType }> = {
  'cch-care-plans': { title: 'Care Plans', description: 'Structured care plan authoring with goals, interventions, and versioning', icon: ClipboardList },
  'cch-authorizations': { title: 'Service Authorizations', description: 'Prior authorization requests, utilization management, and unit tracking', icon: Shield },
  'cch-quality-measures': { title: 'Quality Measures (HEDIS)', description: 'HEDIS measure tracking, member care gaps, and quality reporting', icon: Target },
  'cch-funds-flow': { title: 'Funds Flow Ledger', description: 'Payer-to-hub-to-CBO payment splits, capitation reconciliation, and period close', icon: DollarSign },
  'cch-credentialing': { title: 'Credentialing & Screening', description: 'Provider credentialing lifecycle, OIG/SAM exclusion checks, and document verification', icon: ShieldCheck },
  'cch-discharge-planning': { title: 'Discharge Planning', description: 'ADT-triggered discharge cases, care transitions, and follow-up scheduling', icon: ClipboardCheck },
  'cch-grievances': { title: 'Grievances & Appeals', description: 'Member/provider grievance tracking with regulatory timelines and SLA monitoring', icon: AlertTriangle },
  'cch-outcomes': { title: 'Outcomes Tracking', description: 'Pre/post outcome capture, HRSN resolution tracking, and ROI reporting', icon: TrendingUp },
  'cch-chw-supervision': { title: 'CHW Supervision', description: 'Supervision sessions, encounter review, caseload management, and productivity dashboards', icon: Users },
  'cch-nemt': { title: 'NEMT Transportation', description: 'Non-emergency medical transportation trip requests, broker integration, and ride tracking', icon: MapPin },
  'cch-housing': { title: 'Housing & 211', description: 'Housing referrals, tenancy support workflows, and 211 resource search', icon: Home },
  'cch-accreditation': { title: 'Accreditation Tracking', description: 'NCQA/URAC accreditation standards, evidence collection, and compliance scoring', icon: Award },
  'cch-sponsor-console': { title: 'Sponsor Console', description: 'Sponsor dashboard with network adequacy, CBO performance, and fund deployment analytics', icon: BarChart3 },
};

// ─── Shared UI Components ───────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    active: 'bg-green-100 text-green-800',
    approved: 'bg-green-100 text-green-800',
    verified: 'bg-green-100 text-green-800',
    resolved: 'bg-green-100 text-green-800',
    completed: 'bg-blue-100 text-blue-800',
    closed: 'bg-blue-100 text-blue-800',
    discharged: 'bg-blue-100 text-blue-800',
    draft: 'bg-gray-100 text-gray-600',
    cancelled: 'bg-gray-100 text-gray-600',
    requested: 'bg-yellow-100 text-yellow-800',
    pended: 'bg-yellow-100 text-yellow-800',
    filed: 'bg-yellow-100 text-yellow-800',
    pending: 'bg-yellow-100 text-yellow-800',
    submitted: 'bg-yellow-100 text-yellow-800',
    scheduled: 'bg-yellow-100 text-yellow-800',
    in_transit: 'bg-yellow-100 text-yellow-800',
    denied: 'bg-red-100 text-red-800',
    expired: 'bg-red-100 text-red-800',
    overdue: 'bg-red-100 text-red-800',
    in_progress: 'bg-indigo-100 text-indigo-800',
    under_review: 'bg-indigo-100 text-indigo-800',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-600'}`}>
      {status?.replace(/_/g, ' ')}
    </span>
  );
}

function LoadingState() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" />
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="text-center py-12 text-ink-400">
      <p>{message}</p>
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="text-center py-12">
      <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
        <AlertTriangle className="h-4 w-4 shrink-0" />
        <p>{message}</p>
      </div>
    </div>
  );
}

function BackButton({ onClick }: { onClick: () => void }) {
  return (
    <button onClick={onClick} className="flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-800 mb-4">
      <ArrowLeft className="h-4 w-4" /> Back to list
    </button>
  );
}

function ActionButton({ label, onClick, variant = 'primary', icon: Icon }: {
  label: string; onClick: () => void; variant?: 'primary' | 'danger' | 'secondary'; icon?: React.ElementType;
}) {
  const base = 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors';
  const styles = {
    primary: 'bg-indigo-600 text-white hover:bg-indigo-700',
    danger: 'bg-red-600 text-white hover:bg-red-700',
    secondary: 'bg-gray-100 text-gray-700 hover:bg-gray-200',
  };
  return (
    <button onClick={onClick} className={`${base} ${styles[variant]}`}>
      {Icon && <Icon className="h-3.5 w-3.5" />} {label}
    </button>
  );
}

function ListRow({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  return (
    <div
      onClick={onClick}
      className="p-4 bg-white rounded-lg border border-gray-200 hover:border-indigo-300 hover:shadow-sm transition-all cursor-pointer flex items-center justify-between"
    >
      <div className="flex-1 min-w-0">{children}</div>
      <ChevronRight className="h-4 w-4 text-ink-300 shrink-0 ml-2" />
    </div>
  );
}

function DetailField({ label, value }: { label: string; value: any }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div>
      <dt className="text-xs text-ink-400 uppercase tracking-wide">{label}</dt>
      <dd className="text-sm text-ink-900 mt-0.5">{String(value)}</dd>
    </div>
  );
}

function ProgressBar({ used, total, label }: { used: number; total: number; label?: string }) {
  const pct = total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;
  return (
    <div>
      <div className="flex justify-between text-xs text-ink-500 mb-1">
        <span>{label || `${used} / ${total}`}</span>
        <span>{pct}%</span>
      </div>
      <div className="w-full bg-gray-100 rounded-full h-2">
        <div
          className={`h-2 rounded-full transition-all ${pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-yellow-500' : 'bg-indigo-500'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function SectionHeading({ title, count }: { title: string; count?: number }) {
  return (
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-base font-semibold text-ink-900">{title}</h3>
      {count !== undefined && <span className="text-sm text-ink-500">{count} total</span>}
    </div>
  );
}

function PipelineLinkedBadge() {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
      <Link2 className="h-3 w-3" /> Pipeline Linked
    </span>
  );
}

function ReferralLinkedBadge() {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
      <Link2 className="h-3 w-3" /> Referral Linked
    </span>
  );
}

function CreateReferralButton({ endpoint, onSuccess }: { endpoint: string; onSuccess: () => void }) {
  const [showPicker, setShowPicker] = useState(false);
  const [participants, setParticipants] = useState<any[]>([]);
  const [selectedCbo, setSelectedCbo] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (showPicker && participants.length === 0) {
      apiClient.get('/api/hub-participants').then(res => {
        setParticipants(res.data.data?.items || []);
      }).catch(() => {});
    }
  }, [showPicker]);

  if (!showPicker) {
    return (
      <button
        onClick={() => setShowPicker(true)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
      >
        <Link2 className="h-3.5 w-3.5" /> Create Referral
      </button>
    );
  }

  return (
    <div className="p-4 border border-indigo-200 rounded-lg bg-indigo-50 space-y-3">
      <p className="text-sm font-medium text-indigo-900">Create Service Referral</p>
      <div>
        <label className="block text-xs text-ink-500 mb-1">Assign to participating organization</label>
        <select
          value={selectedCbo || ''}
          onChange={e => setSelectedCbo(e.target.value ? Number(e.target.value) : null)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
        >
          <option value="">Hub org (self)</option>
          {participants.map((p: any) => (
            <option key={p.participant_cbo_id} value={p.participant_cbo_id}>
              {p.participant_name || `CBO #${p.participant_cbo_id}`} ({(p.hub_model_type || '').replace(/_/g, ' ')})
            </option>
          ))}
        </select>
      </div>
      <div className="flex gap-2">
        <button
          disabled={creating}
          onClick={async () => {
            setCreating(true);
            try {
              await apiClient.post(endpoint, { assigned_to_cbo_id: selectedCbo || undefined });
              onSuccess();
            } catch (err) {
              console.error('Failed to create referral:', err);
            } finally {
              setCreating(false);
              setShowPicker(false);
            }
          }}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          {creating ? 'Creating...' : 'Create & Link'}
        </button>
        <button
          onClick={() => setShowPicker(false)}
          className="px-3 py-1.5 rounded-md text-sm text-ink-500 hover:text-ink-700"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function ServicePipelineSection({ referralId }: { referralId: number }) {
  const [referral, setReferral] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.get(`/api/service-referrals/${referralId}`).then(res => {
      setReferral(res.data.data || res.data);
    }).catch(() => setReferral(null)).finally(() => setLoading(false));
  }, [referralId]);

  if (loading) return <div className="text-xs text-ink-400">Loading pipeline...</div>;
  if (!referral) return null;

  return (
    <div className="mt-6 p-4 bg-indigo-50 rounded-lg border border-indigo-200">
      <h4 className="text-sm font-semibold text-ink-700 mb-3 flex items-center gap-2">
        <ExternalLink className="h-4 w-4 text-indigo-600" /> Service Pipeline
      </h4>
      <dl className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
        <DetailField label="Referral ID" value={referral.id} />
        <DetailField label="Status" value={referral.status} />
        <DetailField label="Service Type" value={referral.service_type} />
        <DetailField label="Priority" value={referral.priority} />
        <DetailField label="Created" value={referral.created_at?.slice(0, 10)} />
        <DetailField label="Assigned To" value={referral.assigned_to_name || referral.assigned_to} />
      </dl>
    </div>
  );
}

function checkCbo(): string | null {
  return localStorage.getItem('active_cbo_id');
}

function noCboError(): string {
  return 'No organization selected. Switch to an organization with CCH capabilities enabled.';
}

function apiError(err: any, fallback: string): string {
  const status = err?.response?.status;
  if (status === 403) return 'Your organization does not have this capability enabled.';
  if (status === 404) return 'This module endpoint is not available on the current backend.';
  return err?.response?.data?.detail || fallback;
}

// ─── Care Plans Module ──────────────────────────────────────────────────────

function CarePlansModule() {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchList = useCallback(async () => {
    if (!checkCbo()) { setError(noCboError()); setLoading(false); return; }
    try {
      const res = await apiClient.get('/api/care-plans');
      setItems(res.data.data?.items || []);
    } catch (err: any) { setError(apiError(err, 'Failed to load care plans.')); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    setDetailLoading(true);
    apiClient.get(`/api/care-plans/${selectedId}`).then(res => {
      setDetail(res.data.data);
    }).catch(() => setDetail(null)).finally(() => setDetailLoading(false));
  }, [selectedId]);

  const transition = async (status: string) => {
    if (!selectedId) return;
    await apiClient.post(`/api/care-plans/${selectedId}/transition`, { status });
    const res = await apiClient.get(`/api/care-plans/${selectedId}`);
    setDetail(res.data.data);
    fetchList();
  };

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;

  if (selectedId && detailLoading) return <LoadingState />;
  if (selectedId && detail) return (
    <div>
      <BackButton onClick={() => setSelectedId(null)} />
      <div className="flex items-start justify-between mb-4">
        <h3 className="text-lg font-semibold text-ink-900">{detail.title || `Care Plan #${detail.id}`}</h3>
        <StatusBadge status={detail.status} />
      </div>
      <dl className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <DetailField label="Plan Type" value={detail.plan_type} />
        <DetailField label="Version" value={detail.version_number} />
        <DetailField label="Review Date" value={detail.review_date} />
        <DetailField label="Created" value={detail.created_at?.slice(0, 10)} />
        <DetailField label="Member ID" value={detail.member_id} />
        <DetailField label="Assigned To" value={detail.assigned_to_name || detail.assigned_to} />
      </dl>
      <div className="flex gap-2 mb-6">
        {detail.status === 'draft' && <ActionButton label="Activate" onClick={() => transition('active')} icon={Check} />}
        {detail.status === 'active' && <ActionButton label="Complete" onClick={() => transition('completed')} icon={Check} />}
        {detail.status !== 'cancelled' && detail.status !== 'completed' && (
          <ActionButton label="Cancel" onClick={() => transition('cancelled')} variant="danger" icon={X} />
        )}
      </div>
      {detail.goals && detail.goals.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-ink-700 mb-3">Goals ({detail.goals.length})</h4>
          <div className="space-y-3">
            {detail.goals.map((goal: any, gi: number) => (
              <div key={gi} className="border border-gray-200 rounded-lg p-3">
                <div className="flex items-start justify-between">
                  <p className="font-medium text-sm text-ink-900">{goal.description || goal.title || `Goal ${gi + 1}`}</p>
                  <StatusBadge status={goal.status || 'active'} />
                </div>
                {goal.target_date && <p className="text-xs text-ink-400 mt-1">Target: {goal.target_date}</p>}
                {goal.interventions && goal.interventions.length > 0 && (
                  <div className="mt-2 pl-3 border-l-2 border-indigo-200 space-y-2">
                    {goal.interventions.map((iv: any, ii: number) => (
                      <div key={ii} className="text-xs text-ink-600">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{iv.intervention_type || 'Intervention'}:</span> {iv.description || iv.title}
                          {iv.frequency && <span className="text-ink-400 ml-1">({iv.frequency})</span>}
                          {iv.service_referral_id && <ReferralLinkedBadge />}
                        </div>
                        {!iv.service_referral_id && iv.id && (
                          <div className="mt-1" onClick={e => e.stopPropagation()}>
                            <CreateReferralButton
                              endpoint={`/api/care-plans/interventions/${iv.id}/create-referral`}
                              onSuccess={async () => { const res = await apiClient.get(`/api/care-plans/${selectedId}`); setDetail(res.data.data); }}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      <SectionHeading title="Active Care Plans" count={items.length} />
      {items.length === 0 ? <EmptyState message="No care plans found." /> : (
        <div className="space-y-3">
          {items.map((plan: any) => (
            <ListRow key={plan.id} onClick={() => setSelectedId(plan.id)}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-medium text-ink-900">{plan.title || `Care Plan #${plan.id}`}</p>
                  <p className="text-sm text-ink-500 mt-1">Type: {plan.plan_type} | Version {plan.version_number}</p>
                </div>
                <StatusBadge status={plan.status} />
              </div>
              <div className="mt-2 flex gap-4 text-xs text-ink-400">
                <span>{plan.goal_count || 0} goals</span>
                <span>{plan.goals_achieved || 0} achieved</span>
                {plan.review_date && <span>Review: {plan.review_date}</span>}
              </div>
            </ListRow>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Authorizations Module ──────────────────────────────────────────────────

function AuthorizationsModule() {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchList = useCallback(async () => {
    if (!checkCbo()) { setError(noCboError()); setLoading(false); return; }
    try {
      const res = await apiClient.get('/api/service-authorizations');
      setItems(res.data.data?.items || []);
    } catch (err: any) { setError(apiError(err, 'Failed to load authorizations.')); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    setDetailLoading(true);
    apiClient.get(`/api/service-authorizations/${selectedId}`).then(res => {
      setDetail(res.data.data);
    }).catch(() => setDetail(null)).finally(() => setDetailLoading(false));
  }, [selectedId]);

  const transition = async (status: string, extra?: Record<string, any>) => {
    if (!selectedId) return;
    await apiClient.post(`/api/service-authorizations/${selectedId}/transition`, { status, ...extra });
    const res = await apiClient.get(`/api/service-authorizations/${selectedId}`);
    setDetail(res.data.data);
    fetchList();
  };

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;

  if (selectedId && detailLoading) return <LoadingState />;
  if (selectedId && detail) return (
    <div>
      <BackButton onClick={() => setSelectedId(null)} />
      <div className="flex items-start justify-between mb-4">
        <h3 className="text-lg font-semibold text-ink-900">{detail.service_description || `Auth #${detail.id}`}</h3>
        <StatusBadge status={detail.status} />
      </div>
      <dl className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <DetailField label="Auth Type" value={detail.auth_type} />
        <DetailField label="Service Code" value={detail.service_code} />
        <DetailField label="Auth Number" value={detail.auth_number} />
        <DetailField label="Unit Type" value={detail.unit_type} />
        <DetailField label="Requested Units" value={detail.requested_units} />
        <DetailField label="Approved Units" value={detail.approved_units} />
        <DetailField label="Used Units" value={detail.used_units} />
        <DetailField label="Start Date" value={detail.start_date} />
        <DetailField label="End Date" value={detail.end_date} />
        <DetailField label="Member ID" value={detail.member_id} />
        <DetailField label="Requesting Provider" value={detail.requesting_provider_name || detail.requesting_provider_id} />
      </dl>
      {detail.approved_units && (
        <div className="mb-6">
          <ProgressBar used={detail.used_units || 0} total={detail.approved_units} label={`${detail.used_units || 0} / ${detail.approved_units} ${detail.unit_type || 'units'} used`} />
        </div>
      )}
      <div className="flex gap-2 mb-6">
        {detail.status === 'requested' && (
          <>
            <ActionButton label="Approve" onClick={() => transition('approved')} icon={Check} />
            <ActionButton label="Deny" onClick={() => transition('denied')} variant="danger" icon={X} />
            <ActionButton label="Pend" onClick={() => transition('pended')} variant="secondary" icon={Clock} />
          </>
        )}
        {detail.status === 'pended' && (
          <>
            <ActionButton label="Approve" onClick={() => transition('approved')} icon={Check} />
            <ActionButton label="Deny" onClick={() => transition('denied')} variant="danger" icon={X} />
          </>
        )}
      </div>
      {detail.history && detail.history.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-ink-700 mb-3">Status History</h4>
          <div className="border-l-2 border-gray-200 pl-4 space-y-3">
            {detail.history.map((h: any, i: number) => (
              <div key={i} className="text-sm">
                <div className="flex items-center gap-2">
                  <StatusBadge status={h.status} />
                  <span className="text-xs text-ink-400">{h.changed_at || h.created_at}</span>
                </div>
                {h.reason && <p className="text-xs text-ink-500 mt-1">{h.reason}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <button
          onClick={async () => {
            const referralIdStr = prompt('Enter the Service Referral ID to link:');
            if (!referralIdStr) return;
            const referralId = parseInt(referralIdStr, 10);
            if (isNaN(referralId)) return;
            try {
              await apiClient.post(`/api/service-authorizations/${selectedId}/link-referral`, { referral_id: referralId });
              const res = await apiClient.get(`/api/service-authorizations/${selectedId}`);
              setDetail(res.data.data);
            } catch {}
          }}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
        >
          <Link2 className="h-3.5 w-3.5" /> Link to Referral
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <SectionHeading title="Service Authorizations" count={items.length} />
      {items.length === 0 ? <EmptyState message="No service authorizations found." /> : (
        <div className="space-y-3">
          {items.map((auth: any) => (
            <ListRow key={auth.id} onClick={() => setSelectedId(auth.id)}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-medium text-ink-900">{auth.service_description || auth.service_code || `Auth #${auth.id}`}</p>
                  <p className="text-sm text-ink-500 mt-1">Type: {auth.auth_type} | {auth.unit_type || 'units'}</p>
                </div>
                <StatusBadge status={auth.status} />
              </div>
              {auth.approved_units && (
                <div className="mt-2">
                  <ProgressBar used={auth.used_units || 0} total={auth.approved_units} />
                </div>
              )}
            </ListRow>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Quality Measures Module ────────────────────────────────────────────────

function QualityMeasuresModule() {
  const [rates, setRates] = useState<any[]>([]);
  const [definitions, setDefinitions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [liveMode, setLiveMode] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const year = new Date().getFullYear();

  const load = (live: boolean) => {
    setLoading(true);
    setError(null);
    const liveParam = live ? '&compute=live' : '';
    Promise.all([
      apiClient.get(`/api/quality-measures/rates?measurement_year=${year}${liveParam}`),
      apiClient.get('/api/quality-measures/definitions'),
    ]).then(([ratesRes, defsRes]) => {
      setRates(ratesRes.data.data || []);
      setDefinitions(defsRes.data.data || []);
    }).catch((err: any) => { setError(apiError(err, 'Failed to load quality measures.')); })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!checkCbo()) { setError(noCboError()); setLoading(false); return; }
    load(liveMode);
  }, [year, liveMode]);

  const recompute = async () => {
    setRecomputing(true);
    try {
      await apiClient.post(`/api/quality-measures/recompute?measurement_year=${year}`);
      await apiClient.post(`/api/quality-measures/outcomes/recompute`);
      setLiveMode(true);
      load(true);
    } catch (err: any) {
      setError(apiError(err, 'Recompute failed.'));
    } finally {
      setRecomputing(false);
    }
  };

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3">
        <div className="flex items-center gap-3">
          <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Data mode</span>
          <button
            onClick={() => setLiveMode(false)}
            className={`rounded-full px-3 py-1 text-xs font-medium ${!liveMode ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
          >
            Mock seed
          </button>
          <button
            onClick={() => setLiveMode(true)}
            className={`rounded-full px-3 py-1 text-xs font-medium ${liveMode ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-700'}`}
          >
            Live (computed)
          </button>
          <span className="text-xs text-gray-400">
            {liveMode
              ? 'Rates aggregate from discharge_events, service_referrals, care_plan_interventions, and enrollment_assessments.'
              : 'Rates come from the original seed snapshot.'}
          </span>
        </div>
        <button
          onClick={recompute}
          disabled={recomputing}
          className="rounded-md border border-emerald-300 bg-white px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
        >
          {recomputing ? 'Recomputing…' : 'Recompute live data'}
        </button>
      </div>
      <div>
        <SectionHeading title={`HEDIS Measure Rates - ${year} ${liveMode ? '(live)' : '(seed)'}`} count={rates.length} />
        {rates.length === 0 ? <EmptyState message="No measure data available for this year." /> : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {rates.map((m: any, i: number) => {
              const rate = m.rate_pct != null ? m.rate_pct : m.rate;
              const denom = m.denominator ?? m.denominator_count;
              const numer = m.numerator ?? m.numerator_count;
              return (
                <div key={i} className="p-4 bg-white rounded-lg border border-gray-200">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-ink-900">{m.measure_id_hedis}</p>
                      <p className="text-sm text-ink-500">{m.measure_name}</p>
                      <p className="text-xs text-ink-400 mt-1">{m.domain}</p>
                    </div>
                    <span className={`text-2xl font-bold ${rate >= 80 ? 'text-green-600' : rate >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>
                      {rate != null ? `${rate}%` : 'N/A'}
                    </span>
                  </div>
                  <div className="mt-2 flex gap-3 text-xs text-ink-400">
                    <span>Denom: {denom}</span>
                    <span>Numer: {numer}</span>
                    {m.open_gaps != null && <span className="text-red-500">Open gaps: {m.open_gaps}</span>}
                    {m.seed_origin && (
                      <span className={`ml-auto rounded-full px-1.5 py-0.5 text-[10px] uppercase ${m.seed_origin === 'live' ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>
                        {m.seed_origin}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {definitions.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-ink-700 mb-3">Measure Definitions ({definitions.length})</h4>
          <div className="space-y-2">
            {definitions.map((d: any, i: number) => (
              <div key={i} className="p-3 bg-gray-50 rounded-lg text-sm">
                <p className="font-medium text-ink-900">{d.measure_id_hedis || d.measure_id} - {d.measure_name || d.name}</p>
                <p className="text-xs text-ink-500 mt-1">{d.description || d.domain}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Funds Flow Module ──────────────────────────────────────────────────────

function FundsFlowModule() {
  const [contracts, setContracts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [splitRules, setSplitRules] = useState<any[]>([]);
  const [periodSummary, setPeriodSummary] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    if (!checkCbo()) { setError(noCboError()); setLoading(false); return; }
    apiClient.get('/api/funds-flow/contracts').then(res => {
      setContracts(res.data.data || []);
    }).catch((err: any) => { setError(apiError(err, 'Failed to load funds-flow contracts.')); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedId) { setSplitRules([]); setPeriodSummary(null); return; }
    setDetailLoading(true);
    const contract = contracts.find(c => c.id === selectedId);
    const periodKey = contract?.current_period_key || `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
    Promise.all([
      apiClient.get(`/api/funds-flow/contracts/${selectedId}/split-rules`),
      apiClient.get(`/api/funds-flow/periods/${periodKey}/summary`).catch(() => ({ data: { data: null } })),
    ]).then(([rulesRes, summaryRes]) => {
      setSplitRules(rulesRes.data.data || []);
      setPeriodSummary(summaryRes.data.data);
    }).catch(() => {}).finally(() => setDetailLoading(false));
  }, [selectedId, contracts]);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;

  if (selectedId && detailLoading) return <LoadingState />;
  if (selectedId) {
    const contract = contracts.find(c => c.id === selectedId);
    return (
      <div>
        <BackButton onClick={() => setSelectedId(null)} />
        <div className="flex items-start justify-between mb-4">
          <h3 className="text-lg font-semibold text-ink-900">{contract?.contract_name || `Contract #${selectedId}`}</h3>
          <StatusBadge status={contract?.status || 'active'} />
        </div>
        <dl className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
          <DetailField label="Payer" value={contract?.payer_name} />
          <DetailField label="Contract Type" value={contract?.contract_type} />
          <DetailField label="Admin Fee" value={contract?.admin_fee_pct ? `${contract.admin_fee_pct}%` : undefined} />
          <DetailField label="Start Date" value={contract?.start_date} />
          <DetailField label="End Date" value={contract?.end_date} />
        </dl>
        {splitRules.length > 0 && (
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-ink-700 mb-3">Split Rules</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-gray-200 text-left text-xs text-ink-400 uppercase">
                  <th className="pb-2 pr-4">Entry Type</th><th className="pb-2 pr-4">CBO</th><th className="pb-2 pr-4">Split %</th><th className="pb-2">Effective</th>
                </tr></thead>
                <tbody>
                  {splitRules.map((r: any, i: number) => (
                    <tr key={i} className="border-b border-gray-100">
                      <td className="py-2 pr-4">{r.entry_type}</td>
                      <td className="py-2 pr-4">{r.cbo_name || r.cbo_id}</td>
                      <td className="py-2 pr-4">{r.split_pct != null ? `${r.split_pct}%` : '-'}</td>
                      <td className="py-2 text-xs text-ink-400">{r.effective_date || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {periodSummary && (
          <div>
            <h4 className="text-sm font-semibold text-ink-700 mb-3">Current Period Summary</h4>
            {periodSummary.by_entry_type && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                {periodSummary.by_entry_type.map((e: any, i: number) => (
                  <div key={i} className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-ink-400">{e.entry_type}</p>
                    <p className="text-lg font-bold text-ink-900">${(e.total_amount || 0).toLocaleString()}</p>
                  </div>
                ))}
              </div>
            )}
            {periodSummary.cbo_breakdown && periodSummary.cbo_breakdown.length > 0 && (
              <div>
                <h5 className="text-xs font-semibold text-ink-500 mb-2">CBO Breakdown</h5>
                {periodSummary.cbo_breakdown.map((c: any, i: number) => (
                  <div key={i} className="flex justify-between py-1 text-sm border-b border-gray-100">
                    <span>{c.cbo_name || c.cbo_id}</span>
                    <span className="font-medium">${(c.amount || 0).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <SectionHeading title="Funds-Flow Contracts" count={contracts.length} />
      {contracts.length === 0 ? <EmptyState message="No funds-flow contracts configured." /> : (
        <div className="space-y-3">
          {contracts.map((c: any) => (
            <ListRow key={c.id} onClick={() => setSelectedId(c.id)}>
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-medium text-ink-900">{c.contract_name || `Contract #${c.id}`}</p>
                  <p className="text-sm text-ink-500">{c.payer_name} | {c.contract_type}</p>
                </div>
                <StatusBadge status={c.status} />
              </div>
              {c.admin_fee_pct && <p className="text-xs text-ink-400 mt-2">Hub admin fee: {c.admin_fee_pct}%</p>}
            </ListRow>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Credentialing Module ───────────────────────────────────────────────────

function CredentialingModule() {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchList = useCallback(async () => {
    if (!checkCbo()) { setError(noCboError()); setLoading(false); return; }
    try {
      const res = await apiClient.get('/api/credentialing/applications');
      setItems(res.data.data?.items || []);
    } catch (err: any) { setError(apiError(err, 'Failed to load credentialing applications.')); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    setDetailLoading(true);
    apiClient.get(`/api/credentialing/applications/${selectedId}`).then(res => {
      setDetail(res.data.data);
    }).catch(() => setDetail(null)).finally(() => setDetailLoading(false));
  }, [selectedId]);

  const transition = async (status: string) => {
    if (!selectedId) return;
    await apiClient.post(`/api/credentialing/applications/${selectedId}/transition`, { status });
    const res = await apiClient.get(`/api/credentialing/applications/${selectedId}`);
    setDetail(res.data.data);
    fetchList();
  };

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;

  if (selectedId && detailLoading) return <LoadingState />;
  if (selectedId && detail) {
    const totalDocs = detail.documents?.length || 0;
    const verifiedDocs = detail.documents?.filter((d: any) => d.status === 'verified').length || 0;
    const pendingDocs = detail.documents?.filter((d: any) => d.status === 'pending').length || 0;
    const expiredDocs = detail.documents?.filter((d: any) => d.status === 'expired').length || 0;

    return (
    <div>
      <BackButton onClick={() => setSelectedId(null)} />
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-ink-900">{detail.entity_name || `Application #${detail.id}`}</h3>
          <p className="text-sm text-ink-500">{detail.provider_type === 'individual' ? 'Individual Provider' : 'Organization'} {detail.email && `· ${detail.email}`}</p>
        </div>
        <StatusBadge status={detail.status} />
      </div>

      {/* Status summary banner */}
      <div className={`p-3 rounded-lg mb-6 ${detail.status === 'approved' ? 'bg-green-50 border border-green-200' : detail.status === 'expired' ? 'bg-red-50 border border-red-200' : 'bg-yellow-50 border border-yellow-200'}`}>
        <p className={`text-sm font-medium ${detail.status === 'approved' ? 'text-green-800' : detail.status === 'expired' ? 'text-red-800' : 'text-yellow-800'}`}>
          {detail.status === 'approved' && `Fully credentialed — ${verifiedDocs}/${totalDocs} documents verified. Expires ${detail.expiration_date || 'N/A'}.`}
          {detail.status === 'psv' && `Primary source verification in progress — ${verifiedDocs} of ${totalDocs} documents verified, ${pendingDocs} pending.`}
          {detail.status === 'application' && `Application received — ${pendingDocs} documents pending submission.`}
          {detail.status === 'expired' && `Credential expired${detail.expiration_date ? ` on ${detail.expiration_date}` : ''}. Re-credentialing required. ${expiredDocs} documents expired.`}
          {detail.status === 'committee_review' && `Committee review in progress — all documents verified, awaiting decision.`}
          {detail.status === 'denied' && `Application denied${detail.denial_reason ? `: ${detail.denial_reason}` : '.'}`}
        </p>
      </div>

      <dl className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <DetailField label="Provider Type" value={detail.provider_type === 'individual' ? 'Individual' : 'Organization'} />
        <DetailField label="NPI" value={detail.npi || 'N/A'} />
        <DetailField label="Tax ID" value={detail.tax_id || 'N/A'} />
        <DetailField label="Submitted" value={detail.submitted_at?.slice(0, 10) || detail.created_at?.slice(0, 10)} />
        <DetailField label="PSV Completed" value={detail.psv_completed_at?.slice(0, 10)} />
        <DetailField label="Approved" value={detail.approved_at?.slice(0, 10)} />
        <DetailField label="Expiration" value={detail.expiration_date} />
        <DetailField label="Re-credential Due" value={detail.re_credential_due_date} />
        <DetailField label="Credential Period" value={detail.credential_period_years ? `${detail.credential_period_years} years` : undefined} />
      </dl>

      {detail.notes && (
        <div className="p-3 bg-gray-50 rounded-lg mb-6 text-sm text-ink-600">{detail.notes}</div>
      )}

      {/* Workflow action buttons */}
      <div className="flex gap-2 mb-6">
        {detail.status === 'application' && (
          <ActionButton label="Begin Primary Source Verification" onClick={() => transition('psv')} variant="secondary" icon={Eye} />
        )}
        {detail.status === 'approved' && detail.re_credential_due_date && (
          <div className="text-xs text-ink-400">Re-credentialing due: {detail.re_credential_due_date}</div>
        )}
        {detail.status === 'expired' && (
          <ActionButton label="Start Re-credentialing" onClick={() => transition('application')} variant="secondary" icon={RefreshCw} />
        )}
        {detail.status === 'denied' && (
          <ActionButton label="Resubmit Application" onClick={() => transition('application')} variant="secondary" icon={RefreshCw} />
        )}
      </div>

      {/* Document checklist with verify/reject actions */}
      {detail.documents && detail.documents.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-ink-700">Document Checklist</h4>
            <div className="flex items-center gap-3">
              <span className="text-xs text-ink-400">{verifiedDocs}/{totalDocs} verified</span>
              {totalDocs > 0 && (
                <div className="w-24 bg-gray-200 rounded-full h-2">
                  <div className={`h-2 rounded-full ${verifiedDocs === totalDocs ? 'bg-green-500' : 'bg-indigo-500'}`} style={{ width: `${(verifiedDocs / totalDocs) * 100}%` }} />
                </div>
              )}
            </div>
          </div>
          <div className="space-y-2">
            {detail.documents.map((doc: any, i: number) => (
              <div key={doc.id || i} className={`p-3 rounded-lg border ${doc.status === 'verified' ? 'bg-green-50 border-green-200' : doc.status === 'expired' ? 'bg-red-50 border-red-200' : doc.status === 'rejected' ? 'bg-red-50 border-red-200' : 'bg-white border-gray-200'}`}>
                <div className="flex items-start gap-3">
                  {doc.status === 'verified' ? (
                    <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  ) : doc.status === 'expired' || doc.status === 'rejected' ? (
                    <X className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                  ) : (
                    <Clock className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-medium text-ink-900">{(doc.document_type || '').replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}</p>
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold ${doc.status === 'verified' ? 'bg-green-100 text-green-800' : doc.status === 'expired' ? 'bg-red-100 text-red-800' : doc.status === 'rejected' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>
                        {doc.status?.toUpperCase()}
                      </span>
                      {doc.required && <span className="text-[10px] text-red-500 font-medium">REQUIRED</span>}
                    </div>
                    {doc.notes && <p className="text-xs text-ink-500">{doc.notes}</p>}
                    {doc.verification_notes && doc.status === 'verified' && <p className="text-xs text-green-600 mt-0.5">{doc.verification_notes}</p>}
                    {doc.rejection_reason && <p className="text-xs text-red-600 mt-0.5">Reason: {doc.rejection_reason}</p>}
                    <div className="flex flex-wrap gap-3 mt-1 text-xs text-ink-400">
                      {doc.expiration_date && (
                        <span className={doc.is_expired ? 'text-red-500 font-medium' : ''}>
                          {doc.is_expired ? 'EXPIRED: ' : 'Expires: '}{doc.expiration_date}
                        </span>
                      )}
                      {doc.verified_at && <span>Verified: {typeof doc.verified_at === 'string' ? doc.verified_at.slice(0, 10) : ''}</span>}
                      {doc.file_name && (
                        <span className="text-indigo-600">
                          <FileText className="h-3 w-3 inline mr-0.5" />{doc.file_name}
                          {doc.file_size && ` (${(doc.file_size / 1024).toFixed(0)} KB)`}
                        </span>
                      )}
                      {doc.file_count > 0 && doc.id && (
                        <button
                          onClick={(e) => { e.stopPropagation(); window.open(`/api/documents/${doc.id}/download`, '_blank'); }}
                          className="text-indigo-600 hover:underline cursor-pointer"
                        >
                          Download
                        </button>
                      )}
                    </div>
                  </div>
                  {/* Document actions — uses platform doc verification */}
                  {doc.status === 'pending' && doc.id && (
                    <div className="flex-shrink-0 flex gap-1">
                      <button
                        onClick={async () => {
                          try {
                            await apiClient.put(`/api/documents/${doc.id}`, {
                              verification_status: 'verified',
                              verification_notes: 'Verified during credentialing review',
                            });
                            const res = await apiClient.get(`/api/credentialing/applications/${selectedId}`);
                            setDetail(res.data.data);
                            fetchList();
                          } catch {}
                        }}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-green-600 text-white hover:bg-green-700 transition-colors"
                        title="Mark as verified"
                      >
                        <Check className="h-3 w-3" /> Verify
                      </button>
                      <button
                        onClick={async () => {
                          const reason = window.prompt('Reason for requesting resubmission:');
                          if (!reason) return;
                          try {
                            await apiClient.put(`/api/documents/${doc.id}`, {
                              verification_status: 'rejected',
                              rejection_reason: reason,
                            });
                            const res = await apiClient.get(`/api/credentialing/applications/${selectedId}`);
                            setDetail(res.data.data);
                            fetchList();
                          } catch {}
                        }}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                        title="Request resubmission"
                      >
                        <RefreshCw className="h-3 w-3" /> Request
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Exclusion screening results */}
      {detail.screening_results && detail.screening_results.length > 0 && (
        <div className="mb-6">
          <h4 className="text-sm font-semibold text-ink-700 mb-3">OIG/SAM Exclusion Screening</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {detail.screening_results.map((sr: any, i: number) => (
              <div key={i} className={`p-4 rounded-lg border ${sr.result === 'clear' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-bold text-ink-900">
                      {sr.screening_type === 'oig_leie' ? 'OIG LEIE' : sr.screening_type === 'sam_gov' ? 'SAM.gov' : (sr.screening_type || '').replace(/_/g, ' ').toUpperCase()}
                    </p>
                    <p className="text-xs text-ink-400 mt-1">Last screened: {sr.screening_date}</p>
                    {sr.entity_name && <p className="text-xs text-ink-400">Entity: {sr.entity_name}</p>}
                  </div>
                  <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold ${sr.result === 'clear' ? 'bg-green-200 text-green-900' : 'bg-red-200 text-red-900'}`}>
                    {sr.result === 'clear' ? <Check className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
                    {sr.result === 'clear' ? 'CLEAR' : sr.result === 'match' ? 'MATCH FOUND' : (sr.result || '').toUpperCase()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Committee Review section — shown for PSV and committee_review statuses */}
      {(detail.status === 'psv' || detail.status === 'committee_review') && (
        <div className="p-4 border border-indigo-200 rounded-lg bg-indigo-50">
          <h4 className="text-sm font-semibold text-indigo-900 mb-2">Credentialing Committee Review</h4>
          {detail.status === 'psv' ? (
            <div>
              <p className="text-sm text-indigo-700 mb-3">
                Primary source verification is {verifiedDocs === totalDocs ? 'complete' : 'in progress'}.
                {verifiedDocs < totalDocs && ` ${pendingDocs} document(s) still pending verification.`}
                {verifiedDocs === totalDocs && ' All required documents verified — ready for committee review.'}
              </p>
              {verifiedDocs === totalDocs && (
                <ActionButton label="Submit to Committee" onClick={() => transition('committee_review')} icon={FileText} />
              )}
            </div>
          ) : (
            <div>
              <p className="text-sm text-indigo-700 mb-3">
                Application is under committee review. All {verifiedDocs} documents have been verified. The committee can approve or deny the application.
              </p>
              <div className="flex gap-2">
                <ActionButton label="Approve — Grant Credential" onClick={() => transition('approved')} icon={Check} />
                <ActionButton label="Deny" onClick={() => {
                  const reason = window.prompt('Reason for denial:');
                  if (reason) {
                    apiClient.post(`/api/credentialing/applications/${selectedId}/transition`, { status: 'denied', reason }).then(() => {
                      apiClient.get(`/api/credentialing/applications/${selectedId}`).then(res => { setDetail(res.data.data); fetchList(); });
                    });
                  }
                }} variant="danger" icon={X} />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
  }

  return (
    <div className="space-y-4">
      <SectionHeading title="Credentialing Applications" count={items.length} />
      {items.length === 0 ? <EmptyState message="No credentialing applications found." /> : (
        <div className="space-y-3">
          {items.map((app: any) => (
            <ListRow key={app.id} onClick={() => setSelectedId(app.id)}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-medium text-ink-900">{app.entity_name || `Application #${app.id}`}</p>
                  <p className="text-sm text-ink-500 mt-1">
                    {app.provider_type === 'individual' ? 'Individual' : 'Organization'}
                    {app.npi && ` · NPI: ${app.npi}`}
                    {app.docs_verified != null && app.docs_required != null && ` · Docs: ${app.docs_verified}/${app.docs_required} verified`}
                  </p>
                </div>
                <StatusBadge status={app.status} />
              </div>
            </ListRow>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Discharge Planning Module ──────────────────────────────────────────────

function DischargePlanningModule() {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    if (!checkCbo()) { setError(noCboError()); setLoading(false); return; }
    apiClient.get('/api/discharge-planning/events').then(res => {
      setItems(res.data.data?.items || []);
    }).catch((err: any) => { setError(apiError(err, 'Failed to load discharge events.')); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    setDetailLoading(true);
    apiClient.get(`/api/discharge-planning/events/${selectedId}`).then(res => {
      setDetail(res.data.data);
    }).catch(() => setDetail(null)).finally(() => setDetailLoading(false));
  }, [selectedId]);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;

  if (selectedId && detailLoading) return <LoadingState />;
  if (selectedId && detail) return (
    <div>
      <BackButton onClick={() => setSelectedId(null)} />
      <div className="flex items-start justify-between mb-4">
        <h3 className="text-lg font-semibold text-ink-900">{detail.patient_name || `Event #${detail.id}`}</h3>
        <StatusBadge status={detail.status} />
      </div>
      <dl className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <DetailField label="Event Type" value={detail.event_type} />
        <DetailField label="Facility" value={detail.facility_name} />
        <DetailField label="Admit Date" value={detail.admit_date} />
        <DetailField label="Discharge Date" value={detail.discharge_date} />
        <DetailField label="Disposition" value={detail.disposition} />
        <DetailField label="Follow-Up Date" value={detail.follow_up_date} />
        <DetailField label="Member ID" value={detail.member_id} />
        <DetailField label="Assigned CHW" value={detail.assigned_chw_name || detail.assigned_to} />
      </dl>
      {detail.transition_plan && (
        <div className="p-4 bg-gray-50 rounded-lg">
          <h4 className="text-sm font-semibold text-ink-700 mb-2">Transition Plan</h4>
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <DetailField label="Discharge To" value={detail.transition_plan.discharge_to} />
            <DetailField label="Transportation" value={detail.transition_plan.transportation} />
            <DetailField label="Medications Reconciled" value={detail.transition_plan.medications_reconciled ? 'Yes' : 'No'} />
            <DetailField label="Follow-Up Scheduled" value={detail.transition_plan.follow_up_scheduled ? 'Yes' : 'No'} />
            {detail.transition_plan.notes && (
              <div className="col-span-2">
                <DetailField label="Notes" value={detail.transition_plan.notes} />
              </div>
            )}
          </dl>
        </div>
      )}
      {detail.service_referral_id ? (
        <ServicePipelineSection referralId={detail.service_referral_id} />
      ) : (
        <div className="mt-4">
          <CreateReferralButton
            endpoint={`/api/discharge-planning/events/${detail.id}/create-referral`}
            onSuccess={async () => { const res = await apiClient.get(`/api/discharge-planning/events/${detail.id}`); setDetail(res.data.data); }}
          />
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      <SectionHeading title="Discharge Events" count={items.length} />
      {items.length === 0 ? <EmptyState message="No discharge events found." /> : (
        <div className="space-y-3">
          {items.map((evt: any) => (
            <ListRow key={evt.id} onClick={() => setSelectedId(evt.id)}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-medium text-ink-900">{evt.patient_name || `Event #${evt.id}`}</p>
                  <p className="text-sm text-ink-500 mt-1">{evt.event_type} | {evt.facility_name || 'Unknown facility'}</p>
                </div>
                <div className="flex items-center gap-2">
                  {evt.service_referral_id && <PipelineLinkedBadge />}
                  <StatusBadge status={evt.status} />
                </div>
              </div>
              <div className="mt-1 text-xs text-ink-400">
                {evt.admit_date && <span>Admit: {evt.admit_date}</span>}
                {evt.discharge_date && <span className="ml-3">Discharge: {evt.discharge_date}</span>}
              </div>
            </ListRow>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Grievances Module ──────────────────────────────────────────────────────

function GrievancesModule() {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchList = useCallback(async () => {
    if (!checkCbo()) { setError(noCboError()); setLoading(false); return; }
    try {
      const res = await apiClient.get('/api/grievances');
      setItems(res.data.data?.items || []);
    } catch (err: any) { setError(apiError(err, 'Failed to load grievances.')); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    setDetailLoading(true);
    apiClient.get(`/api/grievances/${selectedId}`).then(res => {
      setDetail(res.data.data);
    }).catch(() => setDetail(null)).finally(() => setDetailLoading(false));
  }, [selectedId]);

  const resolve = async () => {
    if (!selectedId) return;
    const summary = prompt('Enter resolution summary:');
    if (!summary) return;
    await apiClient.post(`/api/grievances/${selectedId}/resolve`, { resolution_summary: summary });
    const res = await apiClient.get(`/api/grievances/${selectedId}`);
    setDetail(res.data.data);
    fetchList();
  };

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;

  if (selectedId && detailLoading) return <LoadingState />;
  if (selectedId && detail) {
    const deadline = detail.regulatory_deadline || detail.due_date;
    const daysLeft = deadline ? Math.ceil((new Date(deadline).getTime() - Date.now()) / 86400000) : null;
    return (
      <div>
        <BackButton onClick={() => setSelectedId(null)} />
        <div className="flex items-start justify-between mb-4">
          <h3 className="text-lg font-semibold text-ink-900">{detail.subject || `Grievance #${detail.id}`}</h3>
          <StatusBadge status={detail.status} />
        </div>
        <dl className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
          <DetailField label="Type" value={detail.grievance_type} />
          <DetailField label="Category" value={detail.category} />
          <DetailField label="Priority" value={detail.priority} />
          <DetailField label="Filed Date" value={detail.filed_date || detail.created_at?.slice(0, 10)} />
          <DetailField label="Member ID" value={detail.member_id} />
          <DetailField label="Assigned To" value={detail.assigned_to_name || detail.assigned_to} />
        </dl>
        {deadline && (
          <div className={`mb-4 p-3 rounded-lg text-sm ${daysLeft !== null && daysLeft <= 5 ? 'bg-red-50 text-red-800' : 'bg-yellow-50 text-yellow-800'}`}>
            <Calendar className="inline h-4 w-4 mr-1" />
            Regulatory deadline: {deadline} {daysLeft !== null && `(${daysLeft > 0 ? `${daysLeft} days remaining` : 'OVERDUE'})`}
          </div>
        )}
        {detail.description && <p className="text-sm text-ink-700 mb-4">{detail.description}</p>}
        {detail.resolution_summary && (
          <div className="mb-4 p-3 bg-green-50 rounded-lg text-sm text-green-800">
            <strong>Resolution:</strong> {detail.resolution_summary}
          </div>
        )}
        {detail.status !== 'resolved' && detail.status !== 'closed' && (
          <div className="mb-6">
            <ActionButton label="Resolve" onClick={resolve} icon={Check} />
          </div>
        )}
        {detail.timeline && detail.timeline.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-ink-700 mb-3">Timeline</h4>
            <div className="border-l-2 border-gray-200 pl-4 space-y-3">
              {detail.timeline.map((t: any, i: number) => (
                <div key={i} className="text-sm">
                  <p className="font-medium text-ink-900">{t.action || t.event}</p>
                  <p className="text-xs text-ink-400">{t.timestamp || t.created_at} {t.actor && `by ${t.actor}`}</p>
                  {t.notes && <p className="text-xs text-ink-500 mt-1">{t.notes}</p>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <SectionHeading title="Grievances & Appeals" count={items.length} />
      {items.length === 0 ? <EmptyState message="No grievances found." /> : (
        <div className="space-y-3">
          {items.map((g: any) => (
            <ListRow key={g.id} onClick={() => setSelectedId(g.id)}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-medium text-ink-900">{g.subject || `Grievance #${g.id}`}</p>
                  <p className="text-sm text-ink-500 mt-1">{g.grievance_type || g.category} | Priority: {g.priority || 'Normal'}</p>
                </div>
                <StatusBadge status={g.status} />
              </div>
            </ListRow>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Outcomes Module ────────────────────────────────────────────────────────

function OutcomesModule() {
  const [definitions, setDefinitions] = useState<any[]>([]);
  const [aggregates, setAggregates] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const year = new Date().getFullYear();

  useEffect(() => {
    if (!checkCbo()) { setError(noCboError()); setLoading(false); return; }
    Promise.all([
      apiClient.get('/api/outcomes/definitions'),
      apiClient.get(`/api/outcomes/aggregate?measurement_year=${year}`),
    ]).then(([defsRes, aggRes]) => {
      setDefinitions(defsRes.data.data?.items || defsRes.data.data || []);
      setAggregates(aggRes.data.data || []);
    }).catch((err: any) => { setError(apiError(err, 'Failed to load outcomes data.')); })
      .finally(() => setLoading(false));
  }, [year]);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;

  return (
    <div className="space-y-6">
      {aggregates.length > 0 && (
        <div>
          <SectionHeading title={`Aggregate Outcomes - ${year}`} count={aggregates.length} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {aggregates.map((a: any, i: number) => (
              <div key={i} className="p-4 bg-white rounded-lg border border-gray-200">
                <p className="font-medium text-ink-900">{a.outcome_name || a.measure_name}</p>
                <div className="flex items-end gap-2 mt-2">
                  <span className="text-2xl font-bold text-indigo-600">{a.rate != null ? `${a.rate}%` : a.value || 'N/A'}</span>
                  {a.target && <span className="text-sm text-ink-400">Target: {a.target}%</span>}
                </div>
                {a.numerator != null && a.denominator != null && (
                  <p className="text-xs text-ink-400 mt-1">{a.numerator} / {a.denominator}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {definitions.length > 0 && (
        <div>
          <SectionHeading title="Outcome Definitions" count={definitions.length} />
          <div className="space-y-2">
            {definitions.map((d: any, i: number) => (
              <div key={i} className="p-3 bg-gray-50 rounded-lg text-sm">
                <p className="font-medium text-ink-900">{d.name || d.outcome_name}</p>
                <p className="text-xs text-ink-500 mt-1">{d.description || d.domain}</p>
                {d.measurement_type && <p className="text-xs text-ink-400 mt-1">Type: {d.measurement_type}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
      {aggregates.length === 0 && definitions.length === 0 && (
        <EmptyState message="No outcomes data available." />
      )}
    </div>
  );
}

// ─── CHW Supervision Module ─────────────────────────────────────────────────

function CHWSupervisionModule() {
  const [sessions, setSessions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!checkCbo()) { setError(noCboError()); setLoading(false); return; }
    apiClient.get('/api/chw-supervision/sessions').then(res => {
      setSessions(res.data.data?.items || []);
    }).catch((err: any) => { setError(apiError(err, 'Failed to load supervision sessions.')); })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;

  return (
    <div className="space-y-4">
      <SectionHeading title="Supervision Sessions" count={sessions.length} />
      {sessions.length === 0 ? <EmptyState message="No supervision sessions found." /> : (
        <div className="space-y-3">
          {sessions.map((s: any, i: number) => (
            <div key={s.id || i} className="p-4 bg-white rounded-lg border border-gray-200">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-medium text-ink-900">{s.supervisor_name || `Session #${s.id || i + 1}`}</p>
                  <p className="text-sm text-ink-500 mt-1">CHW: {s.chw_name || s.supervisee_name || 'N/A'}</p>
                </div>
                <StatusBadge status={s.status || 'completed'} />
              </div>
              <div className="mt-2 flex gap-4 text-xs text-ink-400">
                {s.session_date && <span><Calendar className="inline h-3 w-3 mr-1" />{s.session_date}</span>}
                {s.duration_minutes && <span><Clock className="inline h-3 w-3 mr-1" />{s.duration_minutes} min</span>}
                {s.session_type && <span>{s.session_type}</span>}
              </div>
              {s.notes && <p className="text-xs text-ink-500 mt-2 line-clamp-2">{s.notes}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── NEMT Module ────────────────────────────────────────────────────────────

function NEMTModule() {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    if (!checkCbo()) { setError(noCboError()); setLoading(false); return; }
    apiClient.get('/api/nemt/trips').then(res => {
      setItems(res.data.data?.items || []);
    }).catch((err: any) => { setError(apiError(err, 'Failed to load NEMT trips.')); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    setDetailLoading(true);
    apiClient.get(`/api/nemt/trips/${selectedId}`).then(res => {
      setDetail(res.data.data);
    }).catch(() => setDetail(null)).finally(() => setDetailLoading(false));
  }, [selectedId]);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;

  if (selectedId && detailLoading) return <LoadingState />;
  if (selectedId && detail) return (
    <div>
      <BackButton onClick={() => setSelectedId(null)} />
      <div className="flex items-start justify-between mb-4">
        <h3 className="text-lg font-semibold text-ink-900">Trip #{detail.id}</h3>
        <StatusBadge status={detail.status} />
      </div>
      <dl className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <DetailField label="Member" value={detail.member_name || detail.member_id} />
        <DetailField label="Trip Date" value={detail.trip_date} />
        <DetailField label="Pickup Time" value={detail.pickup_time} />
        <DetailField label="Pickup Address" value={detail.pickup_address} />
        <DetailField label="Dropoff Address" value={detail.dropoff_address} />
        <DetailField label="Trip Type" value={detail.trip_type} />
        <DetailField label="Vehicle Type" value={detail.vehicle_type} />
        <DetailField label="Broker" value={detail.broker_name} />
        <DetailField label="Driver" value={detail.driver_name} />
        <DetailField label="Distance (mi)" value={detail.distance_miles} />
        <DetailField label="Cost" value={detail.cost ? `$${detail.cost}` : undefined} />
        <DetailField label="Appointment Type" value={detail.appointment_type} />
      </dl>
      {detail.service_referral_id ? (
        <ServicePipelineSection referralId={detail.service_referral_id} />
      ) : (
        <div className="mt-4">
          <CreateReferralButton
            endpoint={`/api/nemt/trips/${detail.id}/create-referral`}
            onSuccess={async () => { const res = await apiClient.get(`/api/nemt/trips/${detail.id}`); setDetail(res.data.data); }}
          />
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      <SectionHeading title="NEMT Trips" count={items.length} />
      {items.length === 0 ? <EmptyState message="No NEMT trips found." /> : (
        <div className="space-y-3">
          {items.map((trip: any) => (
            <ListRow key={trip.id} onClick={() => setSelectedId(trip.id)}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-medium text-ink-900">{trip.member_name || `Trip #${trip.id}`}</p>
                  <p className="text-sm text-ink-500 mt-1">{trip.pickup_address} → {trip.dropoff_address}</p>
                </div>
                <div className="flex items-center gap-2">
                  {trip.service_referral_id && <PipelineLinkedBadge />}
                  <StatusBadge status={trip.status} />
                </div>
              </div>
              <div className="mt-1 text-xs text-ink-400">
                {trip.trip_date && <span><Calendar className="inline h-3 w-3 mr-1" />{trip.trip_date}</span>}
                {trip.trip_type && <span className="ml-3">{trip.trip_type}</span>}
              </div>
            </ListRow>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Housing Module ─────────────────────────────────────────────────────────

function HousingModule() {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    if (!checkCbo()) { setError(noCboError()); setLoading(false); return; }
    apiClient.get('/api/housing/referrals').then(res => {
      setItems(res.data.data?.items || []);
    }).catch((err: any) => { setError(apiError(err, 'Failed to load housing referrals.')); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    setDetailLoading(true);
    apiClient.get(`/api/housing/referrals/${selectedId}`).then(res => {
      setDetail(res.data.data);
    }).catch(() => setDetail(null)).finally(() => setDetailLoading(false));
  }, [selectedId]);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;

  if (selectedId && detailLoading) return <LoadingState />;
  if (selectedId && detail) return (
    <div>
      <BackButton onClick={() => setSelectedId(null)} />
      <div className="flex items-start justify-between mb-4">
        <h3 className="text-lg font-semibold text-ink-900">{detail.member_name || `Referral #${detail.id}`}</h3>
        <StatusBadge status={detail.status || 'pending'} />
      </div>
      <dl className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <DetailField label="Housing Type" value={detail.housing_type || detail.referral_type} />
        <DetailField label="Agency" value={detail.agency_name} />
        <DetailField label="Referral Date" value={detail.referral_date} />
        <DetailField label="Unit Type" value={detail.unit_type} />
        <DetailField label="Move-In Date" value={detail.move_in_date} />
        <DetailField label="Member ID" value={detail.member_id} />
      </dl>
      {detail.service_referral_id ? (
        <ServicePipelineSection referralId={detail.service_referral_id} />
      ) : (
        <div className="mt-4">
          <CreateReferralButton
            endpoint={`/api/housing/referrals/${detail.id}/create-referral`}
            onSuccess={async () => { const res = await apiClient.get(`/api/housing/referrals/${detail.id}`); setDetail(res.data.data); }}
          />
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      <SectionHeading title="Housing Referrals" count={items.length} />
      {items.length === 0 ? <EmptyState message="No housing referrals found." /> : (
        <div className="space-y-3">
          {items.map((r: any, i: number) => (
            <ListRow key={r.id || i} onClick={() => setSelectedId(r.id)}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-medium text-ink-900">{r.member_name || `Referral #${r.id || i + 1}`}</p>
                  <p className="text-sm text-ink-500 mt-1">{r.housing_type || r.referral_type || 'Housing'} | {r.agency_name || 'N/A'}</p>
                </div>
                <div className="flex items-center gap-2">
                  {r.service_referral_id && <PipelineLinkedBadge />}
                  <StatusBadge status={r.status || 'pending'} />
                </div>
              </div>
              <div className="mt-2 flex gap-4 text-xs text-ink-400">
                {r.referral_date && <span>Referred: {r.referral_date}</span>}
                {r.unit_type && <span>Unit: {r.unit_type}</span>}
                {r.move_in_date && <span>Move-in: {r.move_in_date}</span>}
              </div>
            </ListRow>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Accreditation Module ───────────────────────────────────────────────────

function AccreditationModule() {
  const [standards, setStandards] = useState<any[]>([]);
  const [cycles, setCycles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCycleId, setSelectedCycleId] = useState<number | null>(null);
  const [cycleSummary, setCycleSummary] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    if (!checkCbo()) { setError(noCboError()); setLoading(false); return; }
    Promise.all([
      apiClient.get('/api/accreditation/standards'),
      apiClient.get('/api/accreditation/cycles'),
    ]).then(([stdRes, cycRes]) => {
      setStandards(stdRes.data.data || []);
      setCycles(cycRes.data.data?.items || []);
    }).catch((err: any) => { setError(apiError(err, 'Failed to load accreditation data.')); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedCycleId) { setCycleSummary(null); return; }
    setDetailLoading(true);
    apiClient.get(`/api/accreditation/cycles/${selectedCycleId}/summary`).then(res => {
      setCycleSummary(res.data.data);
    }).catch(() => setCycleSummary(null)).finally(() => setDetailLoading(false));
  }, [selectedCycleId]);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;

  if (selectedCycleId && detailLoading) return <LoadingState />;
  if (selectedCycleId && cycleSummary) {
    const cycle = cycles.find(c => c.id === selectedCycleId);
    return (
      <div>
        <BackButton onClick={() => setSelectedCycleId(null)} />
        <div className="flex items-start justify-between mb-4">
          <h3 className="text-lg font-semibold text-ink-900">{cycle?.cycle_name || cycleSummary.cycle_name || `Cycle #${selectedCycleId}`}</h3>
          <StatusBadge status={cycle?.status || cycleSummary.status || 'active'} />
        </div>
        <dl className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
          <DetailField label="Accrediting Body" value={cycleSummary.accrediting_body || cycle?.accrediting_body} />
          <DetailField label="Start Date" value={cycleSummary.start_date || cycle?.start_date} />
          <DetailField label="End Date" value={cycleSummary.end_date || cycle?.end_date} />
          <DetailField label="Overall Score" value={cycleSummary.overall_score != null ? `${cycleSummary.overall_score}%` : undefined} />
          <DetailField label="Standards Met" value={cycleSummary.standards_met} />
          <DetailField label="Standards Total" value={cycleSummary.standards_total} />
        </dl>
        {cycleSummary.overall_score != null && cycleSummary.standards_total && (
          <div className="mb-6">
            <ProgressBar used={cycleSummary.standards_met || 0} total={cycleSummary.standards_total} label={`${cycleSummary.standards_met || 0} / ${cycleSummary.standards_total} standards met`} />
          </div>
        )}
        {cycleSummary.by_domain && cycleSummary.by_domain.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-ink-700 mb-3">By Domain</h4>
            <div className="space-y-2">
              {cycleSummary.by_domain.map((d: any, i: number) => (
                <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm text-ink-900">{d.domain_name || d.domain}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium">{d.met || 0} / {d.total || 0}</span>
                    <span className={`text-sm font-bold ${d.score >= 80 ? 'text-green-600' : d.score >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>
                      {d.score != null ? `${d.score}%` : ''}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {cycles.length > 0 && (
        <div>
          <SectionHeading title="Accreditation Cycles" count={cycles.length} />
          <div className="space-y-3">
            {cycles.map((c: any) => (
              <ListRow key={c.id} onClick={() => setSelectedCycleId(c.id)}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium text-ink-900">{c.cycle_name || `Cycle #${c.id}`}</p>
                    <p className="text-sm text-ink-500 mt-1">{c.accrediting_body} | {c.start_date} - {c.end_date}</p>
                  </div>
                  <StatusBadge status={c.status || 'active'} />
                </div>
              </ListRow>
            ))}
          </div>
        </div>
      )}
      {standards.length > 0 && (
        <div>
          <SectionHeading title="Standards" count={standards.length} />
          <div className="space-y-2">
            {standards.map((s: any, i: number) => (
              <div key={i} className="p-3 bg-gray-50 rounded-lg text-sm">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium text-ink-900">{s.standard_id || s.code} - {s.title || s.name}</p>
                    <p className="text-xs text-ink-500 mt-1">{s.domain} | {s.description}</p>
                  </div>
                  {s.required && <span className="text-xs text-red-600 font-medium">Required</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {cycles.length === 0 && standards.length === 0 && (
        <EmptyState message="No accreditation data available." />
      )}
    </div>
  );
}

// ─── Sponsor Console Module ─────────────────────────────────────────────────

function SponsorConsoleModule() {
  const [dashboard, setDashboard] = useState<any>(null);
  const [cboPerformance, setCboPerformance] = useState<any[]>([]);
  const [hubParticipants, setHubParticipants] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!checkCbo()) { setError(noCboError()); setLoading(false); return; }
    Promise.all([
      apiClient.get('/api/sponsor-console/dashboard'),
      apiClient.get('/api/sponsor-console/cbo-performance'),
      apiClient.get('/api/hub-participants').catch(() => ({ data: { data: { items: [] } } })),
    ]).then(([dashRes, perfRes, partRes]) => {
      setDashboard(dashRes.data.data);
      setCboPerformance(perfRes.data.data?.orgs || perfRes.data.data || []);
      setHubParticipants(partRes.data.data?.items || []);
    }).catch((err: any) => { setError(apiError(err, 'Failed to load sponsor console.')); })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;

  return (
    <div className="space-y-6">
      {dashboard && (
        <div>
          <h3 className="text-base font-semibold text-ink-900 mb-3">
            Hub Overview {dashboard.hub_participants_count > 0 && <span className="text-sm font-normal text-ink-400 ml-2">Aggregating across {dashboard.hub_participants_count} participating organizations</span>}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Total Members', value: dashboard.total_members, color: 'text-indigo-600' },
              { label: 'Active Referrals', value: dashboard.active_referrals, color: 'text-blue-600' },
              { label: 'Completed Referrals', value: dashboard.completed_referrals, color: 'text-green-600' },
              { label: 'Active Care Plans', value: dashboard.active_care_plans, color: 'text-purple-600' },
              { label: 'Active Authorizations', value: dashboard.active_authorizations, color: 'text-indigo-600' },
              { label: 'Funds Received', value: dashboard.total_funds_received ? `$${Number(dashboard.total_funds_received).toLocaleString()}` : '$0', color: 'text-green-600' },
              { label: 'Funds Disbursed', value: dashboard.total_funds_disbursed ? `$${Number(dashboard.total_funds_disbursed).toLocaleString()}` : '$0', color: 'text-amber-600' },
              { label: 'Care Gaps Open / Closed', value: `${dashboard.care_gaps_open || 0} / ${dashboard.care_gaps_closed || 0}`, color: 'text-red-600' },
            ].map((kpi) => (
              <div key={kpi.label} className="p-4 bg-white rounded-lg border border-gray-200 text-center">
                <p className="text-xs text-ink-400 uppercase tracking-wide">{kpi.label}</p>
                <p className={`text-2xl font-bold mt-1 ${kpi.color}`}>
                  {typeof kpi.value === 'number' ? kpi.value.toLocaleString() : kpi.value}
                </p>
              </div>
            ))}
          </div>
          {dashboard.participating_orgs?.length > 0 && (
            <div className="mt-4 p-3 bg-indigo-50 rounded-lg">
              <p className="text-xs font-medium text-indigo-700 mb-1">Member Distribution Across Hub</p>
              <div className="flex flex-wrap gap-3">
                {dashboard.participating_orgs.map((org: any) => (
                  <span key={org.cbo_id} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-white border border-indigo-200 text-xs">
                    <span className="font-medium text-ink-700">{org.name}</span>
                    <span className="text-ink-400">{org.member_count} members</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {cboPerformance.length > 0 && (
        <div>
          <SectionHeading title="CBO Performance" count={cboPerformance.length} />
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs text-ink-400 uppercase">
                  <th className="pb-2 pr-4">Organization</th>
                  <th className="pb-2 pr-4">Role</th>
                  <th className="pb-2 pr-4">Members</th>
                  <th className="pb-2 pr-4">Referrals</th>
                  <th className="pb-2 pr-4">Care Plans</th>
                  <th className="pb-2 pr-4">Goals Achieved</th>
                  <th className="pb-2">Funds Disbursed</th>
                </tr>
              </thead>
              <tbody>
                {cboPerformance.map((cbo: any, i: number) => (
                  <tr key={i} className="border-b border-gray-100">
                    <td className="py-2 pr-4 font-medium">{cbo.cbo_name || cbo.name}</td>
                    <td className="py-2 pr-4">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cbo.org_role === 'hub' ? 'bg-indigo-100 text-indigo-800' : 'bg-gray-100 text-gray-600'}`}>
                        {cbo.org_role === 'hub' ? 'Hub Org' : 'Participant'}
                      </span>
                    </td>
                    <td className="py-2 pr-4">{cbo.member_count?.toLocaleString() || '-'}</td>
                    <td className="py-2 pr-4">{cbo.referral_volume || 0} <span className="text-green-600 text-xs">({cbo.completed_referrals || 0} done)</span></td>
                    <td className="py-2 pr-4">{cbo.active_care_plans || 0}</td>
                    <td className="py-2 pr-4">{cbo.goals_achieved || 0}</td>
                    <td className="py-2 font-medium">{cbo.funds_disbursed ? `$${Number(cbo.funds_disbursed).toLocaleString()}` : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {hubParticipants.length > 0 && (
        <div>
          <SectionHeading title="Hub Participants" count={hubParticipants.length} />
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs text-ink-400 uppercase">
                  <th className="pb-2 pr-4">Organization</th>
                  <th className="pb-2 pr-4">Hub Model</th>
                  <th className="pb-2 pr-4">Status</th>
                  <th className="pb-2 pr-4">Members</th>
                  <th className="pb-2 pr-4">Referrals</th>
                  <th className="pb-2">Joined</th>
                </tr>
              </thead>
              <tbody>
                {hubParticipants.map((p: any, i: number) => (
                  <tr key={p.participant_cbo_id || i} className="border-b border-gray-100">
                    <td className="py-2 pr-4 font-medium">{p.participant_name || p.participant_cbo_id}</td>
                    <td className="py-2 pr-4">
                      <span className="px-2 py-0.5 rounded bg-blue-50 text-blue-700 text-xs">{(p.hub_model_type || '').replace(/_/g, ' ')}</span>
                    </td>
                    <td className="py-2 pr-4"><StatusBadge status={p.status || 'active'} /></td>
                    <td className="py-2 pr-4">{p.member_count?.toLocaleString() || '-'}</td>
                    <td className="py-2 pr-4">{p.referral_count || 0}</td>
                    <td className="py-2 text-xs text-ink-400">{p.joined_at?.slice(0, 10) || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {!dashboard && cboPerformance.length === 0 && hubParticipants.length === 0 && (
        <EmptyState message="No sponsor console data available." />
      )}
    </div>
  );
}

// ─── Module Router ──────────────────────────────────────────────────────────

const MODULE_COMPONENTS: Record<string, React.FC> = {
  'cch-care-plans': CarePlansModule,
  'cch-authorizations': AuthorizationsModule,
  'cch-quality-measures': QualityMeasuresModule,
  'cch-funds-flow': FundsFlowModule,
  'cch-credentialing': CredentialingModule,
  'cch-discharge-planning': DischargePlanningModule,
  'cch-grievances': GrievancesModule,
  'cch-outcomes': OutcomesModule,
  'cch-chw-supervision': CHWSupervisionModule,
  'cch-nemt': NEMTModule,
  'cch-housing': HousingModule,
  'cch-accreditation': AccreditationModule,
  'cch-sponsor-console': SponsorConsoleModule,
};

export default function CCHModulePage({ module, onNavigate }: CCHModulePageProps) {
  const tabbed = useTabbedChrome();
  const meta = MODULE_META[module];
  if (!meta) return null;

  const Icon = meta.icon;
  const ModuleComponent = MODULE_COMPONENTS[module];
  const cboId = localStorage.getItem('active_cbo_id');

  return (
    <div className="max-w-6xl mx-auto px-6 py-6">
      <div className="flex items-center gap-3 mb-6">
        {!tabbed && (
        <div className="p-2 rounded-lg bg-indigo-50">
          <Icon className="h-5 w-5 text-indigo-600" />
        </div>
        )}
        <div>
          {!tabbed && <h1 className="text-xl font-bold text-ink-900">{meta.title}</h1>}
          <p className="text-sm text-ink-500">{meta.description}</p>
        </div>
      </div>

      {!cboId && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          No organization selected. Use the command palette (<kbd className="px-1 py-0.5 bg-amber-100 rounded text-xs font-mono">Alt+P</kbd>) to switch to a CCH-enabled organization.
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 p-6">
        {ModuleComponent ? <ModuleComponent /> : <EmptyState message="Module not found." />}
      </div>
    </div>
  );
}
